import { useEffect, useState } from "react";
import { Smartphone, Send, CheckCircle2, AlertTriangle, BellRing } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { FadeUp } from "../components/Reveal";

const CHANNEL = {
  sms: { label: "Sent by text", cls: "bg-emerald-100 text-emerald-800" },
  portal: { label: "Portal only", cls: "bg-slate-100 text-slate-600" },
  "sms-simulated": { label: "Portal only", cls: "bg-slate-100 text-slate-600" },
};

function StatusCard({ status }) {
  const live = status.configured && status.from_number;
  return (
    <div className={`rounded-3xl p-7 text-white grain ${live ? "bg-obsidian" : "bg-[#3B2A1A]"}`} data-testid="sms-status-card">
      <div className="flex items-center gap-3">
        {live ? <CheckCircle2 className="text-emerald-400" size={20} /> : <AlertTriangle className="text-gold" size={20} />}
        <p className="font-serif text-2xl">{live ? "Texting is live" : "Almost there"}</p>
      </div>
      <p className="text-sm text-white/65 mt-3 leading-relaxed" data-testid="sms-status-text">
        {live
          ? `Clients receive real texts from ${status.from_number}. Every message is also saved to their portal feed.`
          : status.configured
            ? "Twilio keys are connected. Verify your mobile in the Twilio console, then buy a number and I'll drop it in — texts start flowing immediately."
            : "Twilio keys are not configured. Notifications stay in the portal feed for now."}
      </p>
      {live && status.trial && (
        <div className="mt-4 rounded-2xl bg-gold/10 border border-gold/30 p-4 text-xs text-gold/90 leading-relaxed" data-testid="sms-trial-notice">
          <span className="font-semibold">Trial mode.</span> Twilio only lets trial accounts text your own verified number, and swaps custom wording for a generic reminder template. Upgrade the account (add a card in the Twilio console) to text any client with the full Mixed By Empress wording.
        </div>
      )}
      <div className="flex gap-6 mt-6">
        <div><p className="font-serif text-3xl" data-testid="sms-sent-count">{status.sent_count}</p><p className="text-[10px] font-mono uppercase tracking-widest text-white/40">Texts sent</p></div>
        <div><p className="font-serif text-3xl" data-testid="sms-failed-count">{status.failed_count}</p><p className="text-[10px] font-mono uppercase tracking-widest text-white/40">Failed</p></div>
      </div>
    </div>
  );
}

function SettingsCard({ status, reload }) {
  const [phone, setPhone] = useState(status.admin_phone || "");
  const [enabled, setEnabled] = useState(status.alerts_enabled ?? true);
  const [testTo, setTestTo] = useState("");
  useEffect(() => { setPhone(status.admin_phone || ""); setEnabled(status.alerts_enabled ?? true); }, [status]);

  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/admin/sms/settings", { admin_phone: phone, alerts_enabled: enabled }); toast.success("Alert settings saved"); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const sendTest = async () => {
    try { const r = await api.post("/admin/sms/test", { to: testTo || phone }); toast.success(`Test text queued (${r.data.status})`); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <form onSubmit={save} className="bg-white rounded-3xl border border-purple-100 p-7 space-y-5" data-testid="sms-settings-card">
      <p className="font-serif text-2xl text-obsidian flex items-center gap-2"><BellRing size={18} className="text-gold" /> Your alerts</p>
      <label className="grid gap-1">
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 pl-1">Empress's mobile — new requests & deposits</span>
        <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 615 555 0123" data-testid="sms-admin-phone-input"
          className="rounded-xl border border-purple-100 px-3 py-2.5 text-sm" />
      </label>
      <label className="flex items-center gap-3 text-sm text-slate-600">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="sms-alerts-toggle" className="accent-[#7C3AED] w-4 h-4" />
        Text me when a booking request or deposit comes in
      </label>
      <button type="submit" data-testid="sms-settings-save" className="px-6 py-2.5 rounded-full bg-gold text-obsidian text-xs font-semibold hover:brightness-110 transition-all">Save</button>
      <div className="pt-5 border-t border-purple-50 flex flex-wrap gap-3 items-center">
        <input type="tel" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Send a test to… (defaults to your mobile)" data-testid="sms-test-input"
          className="flex-1 min-w-[200px] rounded-xl border border-purple-100 px-3 py-2.5 text-sm" />
        <button type="button" onClick={sendTest} data-testid="sms-test-send" className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-obsidian text-white text-xs hover:text-gold transition-colors">
          <Send size={13} /> Send test
        </button>
      </div>
    </form>
  );
}

export default function AdminSms() {
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const reload = () => {
    api.get("/admin/sms/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/admin/sms/log").then((r) => setLog(r.data)).catch(() => {});
  };
  useEffect(() => { reload(); }, []);

  return (
    <div data-testid="admin-sms">
      <FadeUp>
        <h1 className="font-serif text-3xl sm:text-4xl text-obsidian">Text <span className="italic text-purple-700">messages</span></h1>
        <p className="text-sm text-slate-500 mt-2 flex items-center gap-2"><Smartphone size={14} className="text-gold" /> Booking confirmations, reschedules and deposit receipts go out automatically. Alerts for you land on your own phone.</p>
      </FadeUp>
      {status && (
        <div className="mt-10 grid gap-6 lg:grid-cols-2 items-start">
          <StatusCard status={status} />
          <SettingsCard status={status} reload={reload} />
        </div>
      )}
      <FadeUp delay={0.1} className="mt-8 bg-white rounded-3xl border border-purple-100 p-7" data-testid="sms-log">
        <p className="font-serif text-2xl text-obsidian">Message log</p>
        <div className="mt-5 space-y-2">
          {log.map((n) => {
            const c = CHANNEL[n.channel] || CHANNEL.portal;
            return (
              <div key={n.notification_id} className="flex flex-wrap items-start gap-3 p-4 rounded-2xl bg-lilac-mist text-sm" data-testid={`sms-log-${n.notification_id}`}>
                <div className="flex-1 min-w-[240px]">
                  <p className="text-obsidian leading-relaxed">{n.message}</p>
                  <p className="text-[11px] text-slate-400 mt-1 font-mono">{new Date(n.created_at).toLocaleString()} · {n.user_id === "admin" ? "To Empress" : n.email || "client"}{n.phone ? ` · ${n.phone}` : ""}</p>
                  {n.sms_error && <p className="text-[11px] text-red-500 mt-1">{n.sms_error}</p>}
                </div>
                <span className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full ${n.sms_status === "failed" ? "bg-red-100 text-red-700" : n.trial_template ? "bg-amber-100 text-amber-800" : c.cls}`}>
                  {n.sms_status === "failed" ? "Text failed" : n.trial_template ? "Texted (trial template)" : c.label}
                </span>
              </div>
            );
          })}
          {log.length === 0 && <p className="text-sm text-slate-400 italic">No messages yet.</p>}
        </div>
      </FadeUp>
    </div>
  );
}
